import { useCallback } from 'react';

import { logError } from '@zg-rentals/log-error';
import {
  type RentalManagerUserPropertyCriteriaWebApiHandler_set_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args,
  useRentalManagerUserPropertyCriteriaWebApiHandler_set,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import {
  mapCriterionToMultiplexListingCriterionRequest,
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexListingFlow,
  useMultiplexSetListingCriteria,
} from './queries/multiplex';

import type { Client } from '@zg-rentals/http-client';
import type { MutateOptions, UseMutationOptions, UseMutationResult } from '@zg-rentals/react-query5';
import type { UserPropertyCriteriaSetRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { CriterionProps } from '../model/ListingCriteria/Criterion';
import type { UseMultiplexSetListingCriteriaOptions } from './queries/multiplex/useMultiplexSetListingCriteria';

export type SetListingCriteriaMutationVariables = {
  getParams: { listingAlias: string };
  postData: Array<CriterionProps>;
};

type SetListingCriteriaMutationResult = UseMutationResult<
  unknown,
  unknown,
  SetListingCriteriaMutationVariables,
  unknown
>;

type SetListingCriteriaMutationOptions = Omit<
  UseMutationOptions<unknown, unknown, SetListingCriteriaMutationVariables, unknown>,
  'mutationFn'
> & { httpClient?: Client };

type StandardMutationOptions = NonNullable<Parameters<typeof useRentalManagerUserPropertyCriteriaWebApiHandler_set>[0]>;

/**
 * Drop-in replacement for useRentalManagerUserPropertyCriteriaWebApiHandler_set that routes to the multiplex
 * session API during the wizard (`!isOnReviewStep && isInMultiplexListingFlow`), and to the listing API otherwise.
 */
export const useSetListingCriteriaMutation = (
  options?: SetListingCriteriaMutationOptions,
): SetListingCriteriaMutationResult => {
  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const saveViaMultiplex = isOnReviewStep === false && Boolean(isInMultiplexListingFlow);

  const standardMutation = useRentalManagerUserPropertyCriteriaWebApiHandler_set({
    meta: options?.meta,
    httpClient: options?.httpClient,
    onSuccess: options?.onSuccess as StandardMutationOptions['onSuccess'],
    onError: options?.onError as StandardMutationOptions['onError'],
  });

  const multiplexOptions: UseMultiplexSetListingCriteriaOptions = {
    meta: options?.meta,
    httpClient: options?.httpClient,
    onSuccess: options?.onSuccess as UseMultiplexSetListingCriteriaOptions['onSuccess'],
    onError: options?.onError as UseMultiplexSetListingCriteriaOptions['onError'],
  };
  const multiplexMutation = useMultiplexSetListingCriteria(multiplexOptions);

  const buildMultiplexMutationArgs = useCallback(
    (
      variables: SetListingCriteriaMutationVariables,
    ): RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args | null => {
      if (!multiplexBuildingSessionId) {
        logError({
          error: new Error('Unable to save: missing multiplexBuildingSessionId'),
          context: { details: 'useSetListingCriteriaMutation', fieldName: 'listingCriteria' },
        });
        return null;
      }

      return {
        getParams: { multiplexBuildingSessionId },
        postData: {
          listingCriteria: variables.postData.map(mapCriterionToMultiplexListingCriterionRequest),
        },
      };
    },
    [multiplexBuildingSessionId],
  );

  const buildStandardMutationArgs = useCallback(
    (variables: SetListingCriteriaMutationVariables): RentalManagerUserPropertyCriteriaWebApiHandler_set_Args => ({
      getParams: variables.getParams,
      postData: variables.postData as Array<UserPropertyCriteriaSetRequestWebDto>,
    }),
    [],
  );

  const mutate = useCallback(
    (variables: SetListingCriteriaMutationVariables, mutateOptions?: Parameters<typeof standardMutation.mutate>[1]) => {
      if (saveViaMultiplex) {
        const args = buildMultiplexMutationArgs(variables);
        if (!args) {
          return;
        }

        return multiplexMutation.mutate(args, mutateOptions as Parameters<typeof multiplexMutation.mutate>[1]);
      }

      return standardMutation.mutate(
        buildStandardMutationArgs(variables),
        mutateOptions as Parameters<typeof standardMutation.mutate>[1],
      );
    },
    [buildMultiplexMutationArgs, buildStandardMutationArgs, saveViaMultiplex, standardMutation, multiplexMutation],
  );

  const mutateAsync = useCallback(
    async (
      variables: SetListingCriteriaMutationVariables,
      mutateOptions?: MutateOptions<unknown, unknown, SetListingCriteriaMutationVariables, unknown>,
    ) => {
      if (saveViaMultiplex) {
        const args = buildMultiplexMutationArgs(variables);
        if (!args) {
          return;
        }

        return multiplexMutation.mutateAsync(
          args,
          mutateOptions as Parameters<typeof multiplexMutation.mutateAsync>[1],
        );
      }

      return standardMutation.mutateAsync(
        buildStandardMutationArgs(variables),
        mutateOptions as Parameters<typeof standardMutation.mutateAsync>[1],
      );
    },
    [buildMultiplexMutationArgs, buildStandardMutationArgs, saveViaMultiplex, standardMutation, multiplexMutation],
  );

  const activeMutation = saveViaMultiplex ? multiplexMutation : standardMutation;

  return {
    ...(activeMutation as unknown as SetListingCriteriaMutationResult),
    mutate,
    mutateAsync,
    isPending: activeMutation.isPending,
  } as SetListingCriteriaMutationResult;
};
